import { useState, useEffect, useCallback } from 'react';

/**
 * Convert course IDs to compact format for URL efficiency
 * Examples: 
 * 1FA002_y2025_s1_p1 → 1FA002y25s1p1
 * 1FA002_y2025_s1 → 1FA002y25s1
 * 1FA694_instance0p2 → 1FA694i0p2
 */
const compactifyIds = (ids) => {
  return ids.map(id => {
    // Handle the compact format: remove underscores and shorten year
    return id
      .replace(/_y(\d{4})/, (match, year) => `y${year.slice(-2)}`) // _y2025 → y25
      .replace(/_s/g, 's') // _s → s
      .replace(/_p/g, 'p') // _p → p
      .replace(/_instance(\d+)/g, 'i$1'); // _instance0 → i0
  });
};

/**
 * Expand compact course IDs back to full format for internal processing
 * Examples:
 * 1FA002y25s1p1 → 1FA002_y2025_s1_p1
 * 1FA002y25s1 → 1FA002_y2025_s1
 * 1FA694i0p2 → 1FA694_instance0p2
 */
const expandCompactIds = (compactIds) => {
  return compactIds.map(id => {
    // Check if this is already in old format (contains "_y" or "_instance")
    if (id.includes('_y') || id.includes('_instance')) {
      return id; // Return as-is for backward compatibility
    }
    
    // Expand compact format back to full format
    return id
      .replace(/y(\d{2})/, (match, shortYear) => {
        // Convert 2-digit year back to 4-digit (assume 20xx for years 00-99)
        const fullYear = `20${shortYear}`;
        return `_y${fullYear}`;
      })
      .replace(/s(\d+)/g, '_s$1') // s1 → _s1
      .replace(/p(\d+)/g, '_p$1') // p1 → _p1
      .replace(/i(\d+)/g, '_instance$1'); // i0 → _instance0
  });
};

/**
 * Custom hook for URL state management
 * Handles serialization and deserialization of course selection state to/from URL
 * @param {Object} groupedCourses - All grouped courses
 * @param {Set} selectedIds - Set of selected course IDs
 * @param {Set} backupIds - Set of backup course IDs
 * @param {string} viewMode - Current view mode ('selection', 'review', 'timetable')
 * @returns {Object} - URL state management functions
 */
export const useUrlState = (groupedCourses, selectedIds, backupIds, viewMode) => {
  const [urlState, setUrlState] = useState(null);

  /**
   * Serialize current state to URL string using compact format
   * @returns {string} - Complete URL with state parameters
   */
  const serializeStateToUrl = useCallback(() => {
    const selectedArray = Array.from(selectedIds);
    const backupArray = Array.from(backupIds);
    const allSelectedIds = [...selectedArray, ...backupArray];
    
    // Find custom courses from selected/backup courses
    const customCoursesMap = new Map();
    
    allSelectedIds.forEach(id => {
      // Find the course in grouped courses
      Object.values(groupedCourses).forEach(group => {
        const course = group.courses.find(c => {
          const courseId = c.is_split_course 
            ? `${c.semester_instance}_p${c.actual_period}` 
            : c.semester_instance;
          return courseId === id;
        });
        
        if (course && course.isCustomCourse) {
          // Use course_code as key to avoid duplicates for split courses
          if (!customCoursesMap.has(course.course_code)) {
            // Store minimal custom course data
            customCoursesMap.set(course.course_code, {
              course_name: course.course_name,
              course_code: course.course_code,
              credits: course.original_credits || course.credits,
              instances: course.instances,
              notes: course.notes,
              isCustomCourse: true
            });
          }
        }
      });
    });
    
    const params = new URLSearchParams();
    
    // Use compact format for selected and backup course IDs with short parameter names
    if (selectedArray.length > 0) {
      const compactSelected = compactifyIds(selectedArray);
      params.set('s', compactSelected.join(','));
    }
    if (backupArray.length > 0) {
      const compactBackup = compactifyIds(backupArray);
      params.set('b', compactBackup.join(','));
    }
    
    if (customCoursesMap.size > 0) {
      // Encode custom courses as JSON with short parameter name
      const customCoursesArray = Array.from(customCoursesMap.values());
      params.set('c', encodeURIComponent(JSON.stringify(customCoursesArray)));
    }
    
    // Include view mode if it's not the default with ultra-compact names
    if (viewMode && viewMode !== 'selection') {
      // Map internal view mode to ultra-compact URL values
      const urlViewMode = viewMode === 'review' ? 'c' : viewMode === 'timetable' ? 't' : viewMode;
      params.set('v', urlViewMode);
    }
    
    const baseUrl = window.location.origin + window.location.pathname;
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  }, [groupedCourses, selectedIds, backupIds, viewMode]);

  /**
   * Deserialize state from current URL (supports both old and new compact formats)
   * URL view parameter uses: 'selection', 'compact', 'timetable'
   * @returns {Object} - { selectedSet, backupSet, customCourses, viewMode }
   */
  const deserializeStateFromUrl = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    
    // Support both old and new parameter names for backward compatibility
    const selectedParam = params.get('s') || params.get('selected');
    const backupParam = params.get('b') || params.get('backup');
    const customParam = params.get('c') || params.get('custom');
    const viewParam = params.get('v') || params.get('view');
    
    // Handle both compact and legacy formats
    let selectedIds = [];
    let backupIds = [];
    
    if (selectedParam) {
      const rawSelectedIds = selectedParam.split(',').filter(id => id);
      selectedIds = expandCompactIds(rawSelectedIds);
    }
    
    if (backupParam) {
      const rawBackupIds = backupParam.split(',').filter(id => id);
      backupIds = expandCompactIds(rawBackupIds);
    }
    
    const selectedSet = new Set(selectedIds);
    const backupSet = new Set(backupIds);
    
    let customCourses = [];
    if (customParam) {
      try {
        customCourses = JSON.parse(decodeURIComponent(customParam));
      } catch (error) {
        console.error('Failed to parse custom courses from URL:', error);
        customCourses = [];
      }
    }
    
    // Parse view mode, default to 'selection' if invalid or missing
    // Map both old and new URL values back to internal view mode values
    const urlToInternalViewMode = {
      // New ultra-compact format
      's': 'selection',
      'c': 'review',
      't': 'timetable',
      // Old format for backward compatibility
      'selection': 'selection',
      'compact': 'review',
      'timetable': 'timetable'
    };
    const parsedViewMode = urlToInternalViewMode[viewParam] || 'selection';
    
    return { selectedSet, backupSet, customCourses, viewMode: parsedViewMode };
  }, []);

  /**
   * Copy current state URL to clipboard
   * @returns {Promise<string>} - The URL that was copied
   */
  const copyUrlToClipboard = useCallback(async () => {
    const urlWithState = serializeStateToUrl();
    try {
      await navigator.clipboard.writeText(urlWithState);
      return urlWithState;
    } catch (error) {
      console.error('Failed to copy URL to clipboard:', error);
      throw error;
    }
  }, [serializeStateToUrl]);

  // Load URL state once on mount and store it
  useEffect(() => {
    const { selectedSet, backupSet, customCourses, viewMode: urlViewMode } = deserializeStateFromUrl();
    setUrlState({ selectedSet, backupSet, customCourses, viewMode: urlViewMode });
  }, [deserializeStateFromUrl]); // Run only once on mount

  return {
    urlState,
    setUrlState,
    serializeStateToUrl,
    deserializeStateFromUrl,
    copyUrlToClipboard
  };
}; 